    <section
      class="group relative overflow-hidden rounded-xl border border-line bg-card flex flex-col h-full min-w-0 shadow-sm transition-shadow hover:shadow-md"
      [class.ring-1]="filtered"
      [class.ring-accent]="filtered"
    >
      <!-- Header Area -->
      @if (!hideHeader) {
      <div class="relative px-[18px] py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 border-b border-line shrink-0">
        <!-- Title and Subtitle/Breadcrumbs -->
        <div class="min-w-0 w-full sm:flex-1 flex flex-col justify-center">
          <div class="flex min-w-0 items-center gap-4">
            @if (title) {
              <h3 class="shrink-0 text-[18px] font-bold text-ink leading-tight truncate select-none">{{ title }}</h3>
            } @else {
              <div class="h-5 bg-line rounded w-32 animate-pulse"></div>
            }
            <ng-content select="[chartCardTitleActions]" />
          </div>
          @if (breadcrumb && breadcrumb.length > 0) {
            <nav class="flex min-w-0 max-w-full items-center gap-1.5 text-[14px] font-medium text-dim mt-1 truncate select-none">
              @for (item of breadcrumb; track $index) {
                @if ($index > 0) {
                  <span class="text-muted text-[10px] select-none">›</span>
                }
                <span
                  [class.text-ink]="$last"
                  [class.font-bold]="$last"
                  [class.font-medium]="!$last"
                  class="text-[14px] truncate"
                >{{ item }}</span>
              }
              @if (filtered) {
                <button
                  type="button"
                  class="ml-1 inline-flex shrink-0 items-center gap-1 rounded-full border border-line bg-page px-2 py-0.5 text-[11px] font-semibold text-dim transition-colors hover:border-negative/40 hover:text-negative"
                  (click)="breadcrumbClick.emit(breadcrumb.length - 1)"
                  aria-label="Clear filter"
                  title="Clear filter"
                >
                  <i class="pi pi-times text-[9px]"></i>
                  Clear
                </button>
              }
            </nav>
          } @else if (subtitle) {
            <p class="text-[14px] font-medium text-dim mt-1 truncate select-none">{{ subtitle }}</p>
          }
        </div>

        <!-- Drill Control Toolbar -->
        <div class="flex items-center gap-0.5 shrink-0 self-end sm:self-center select-none">
          <ng-content select="[chartCardActions]" />

          @if (filtered && (!breadcrumb || breadcrumb.length === 0)) {
            <button
              type="button"
              class="mr-1 inline-flex shrink-0 items-center gap-1 rounded-full border border-line bg-page px-2.5 py-1 text-[11px] font-semibold text-dim transition-colors hover:border-negative/40 hover:text-negative"
              (click)="breadcrumbClick.emit(0)"
              aria-label="Clear results filter"
              title="Clear results filter"
            >
              <i class="pi pi-times text-[9px]"></i>
              Clear results filter
            </button>
          }

          <!-- Drill Up -->
          @if (!hideDrillUp) {
            <button
              type="button"
              [disabled]="disableDrillUp"
              (click)="drillUp.emit()"
              class="group/button h-7 w-7 rounded bg-transparent hover:bg-page dark:hover:bg-chart flex items-center justify-center transition-colors border-0 cursor-pointer"
              [class.opacity-30]="disableDrillUp"
              [class.cursor-not-allowed]="disableDrillUp"
              aria-label="Drill Up"
            >
              <i class="pi pi-arrow-up inline-flex h-3.5 w-3.5 items-center justify-center text-[14px] leading-none text-muted group-hover/button:text-dim"></i>
            </button>
          }

          <!-- Drill Down -->
          @if (!hideDrillDown) {
            <button
              type="button"
              [disabled]="disableDrillDown"
              (click)="drillDown.emit()"
              class="group/button h-7 w-7 rounded bg-transparent hover:bg-page dark:hover:bg-chart flex items-center justify-center transition-colors border-0 cursor-pointer"
              [class.opacity-30]="disableDrillDown"
              [class.cursor-not-allowed]="disableDrillDown"
              aria-label="Drill Down"
            >
              <i class="pi pi-arrow-down inline-flex h-3.5 w-3.5 items-center justify-center text-[14px] leading-none text-muted group-hover/button:text-dim"></i>
            </button>
          }

          <!-- Next hierarchy level -->
          @if (!hideNextLevel) {
            <button
              type="button"
              [disabled]="disableNextLevel"
              (click)="nextLevel.emit()"
              class="group/button h-7 w-7 rounded bg-transparent hover:bg-page dark:hover:bg-chart flex items-center justify-center transition-colors border-0 cursor-pointer"
              [class.opacity-30]="disableNextLevel"
              [class.cursor-not-allowed]="disableNextLevel"
              aria-label="Next hierarchy level"
            >
              <i class="pi pi-angle-double-right inline-flex h-3.5 w-3.5 items-center justify-center text-[14px] leading-none text-muted group-hover/button:text-dim"></i>
            </button>
          }

          <!-- Expand all -->
          @if (!hideExpandAll) {
            <button
              type="button"
              [disabled]="disableExpandAll"
              (click)="expandAll.emit()"
              class="group/button h-7 w-7 rounded bg-transparent hover:bg-page dark:hover:bg-chart flex items-center justify-center transition-colors border-0 cursor-pointer"
              [class.opacity-30]="disableExpandAll"
              [class.cursor-not-allowed]="disableExpandAll"
              aria-label="Expand all"
            >
              <i class="pi pi-chevron-down inline-flex h-3.5 w-3.5 items-center justify-center text-[14px] leading-none text-muted group-hover/button:text-dim"></i>
            </button>
          }

          <!-- Open details -->
          @if (!hideOpenDetails) {
            <button
              type="button"
              [disabled]="disableOpenDetails"
              (click)="openDetails.emit()"
              class="group/button h-7 rounded bg-transparent hover:bg-page dark:hover:bg-chart flex items-center justify-center gap-1.5 transition-colors border-0 cursor-pointer"
              [class.w-7]="!openDetailsLabel"
              [class.px-2]="openDetailsLabel"
              [class.opacity-30]="disableOpenDetails"
              [class.cursor-not-allowed]="disableOpenDetails"
              [attr.aria-label]="openDetailsLabel || 'Open details'"
            >
              @if (openDetailsLabel) {
                <i class="pi pi-table inline-flex h-3.5 w-3.5 items-center justify-center text-[13px] leading-none text-muted group-hover/button:text-dim"></i>
                <span class="text-[11px] font-semibold text-dim group-hover/button:text-ink">{{ openDetailsLabel }}</span>
              } @else {
                <i class="pi pi-external-link inline-flex h-3.5 w-3.5 items-center justify-center text-[14px] leading-none text-muted group-hover/button:text-dim"></i>
              }
            </button>
          }
        </div>
      </div>
      }

      <!-- Card Body -->
      <div class="relative flex-1 min-h-0 flex flex-col px-[18px] pb-4 pt-4">
        <ng-content />
      </div>
    </section>
  
